import { Tag } from 'antd';
import React from 'react';

type MemberLike = {
  id: number;
  name?: string | null;
  username?: string | null;
  blocked?: boolean | null;
};

export type MemberOption = {
  value: string | number;
  label: React.ReactNode;
};

export function getMemberName(user?: MemberLike | null): string {
  if (!user) return '';
  if (user.username) {
    return `${user.name} (${user.username})`;
  }
  return user.name || '';
}

// "Disabled" is the admin Disable button (`blocked`); `blocked_login` users are still selectable
export const isMemberDisabled = (member?: MemberLike | null) => !!member?.blocked;

const toIdSet = (selected: unknown) =>
  new Set(
    (Array.isArray(selected) ? selected : [selected])
      .filter(v => v !== undefined && v !== null && v !== '')
      .map(v => `${typeof v === 'object' ? (v as MemberLike).id : v}`),
  );

/**
 * Disabled members are left out of the options unless they are already selected, in which
 * case they stay, greyed out, so existing records don't render a raw id. They are not marked
 * `disabled`: that would stop a multi-select from removing them.
 */
export function toMemberOptions(
  members: MemberLike[] | null | undefined,
  selected: unknown,
  {
    valueAsString = false,
    disabledLabel = 'Disabled',
    getLabel = getMemberName,
  }: {
    valueAsString?: boolean;
    disabledLabel?: string;
    getLabel?: (member: MemberLike) => string;
  } = {},
): MemberOption[] {
  const selectedIds = toIdSet(selected);
  return (members || [])
    .filter(member => !isMemberDisabled(member) || selectedIds.has(`${member.id}`))
    .map(member => {
      const value = valueAsString ? `${member.id}` : member.id;
      if (!isMemberDisabled(member)) {
        return { value, label: getLabel(member) };
      }
      return {
        value,
        label: (
          <span className="text-gray-400">
            {getLabel(member)}
            <Tag color="default" className="ml-2">
              {disabledLabel}
            </Tag>
          </span>
        ),
      };
    });
}
